<template>
  <template v-if="partnerContent === undefined || !isDirectedAdSlot">
    <LazyClassicNtpCommonOutbrainAdsWidgetWithFixCss
      v-if="props.fallback === 'outbrain'"
      :grid-position="props?.gridPosition"
      :widget-id="props?.widgetId"
      :counter="props?.counter"
    />
    <LazyClassicNtpCommonTaboolaAdsV5
      v-if="props.fallback === 'taboola'"
      :grid-position="props?.gridPosition"
      :counter="props?.counter"
      :second-ad-tile="props?.secondAdTile"
    />
  </template>

  <template v-if="partnerContent !== undefined && isDirectedAdSlot">
    <a @click="handleDirectAdClick">
      <div class="news-v5__item-content">
        <div class="news-v5__item-image" :style="`background-image: url('${getImageLink()}')`"></div>
        <div class="news-v5__item-title text-gray-900 dark:text-gray-400">
          <p>{{ partnerContent?.Headline }}</p>
        </div>
      </div>
      <div class="news-v5__item-provider text-end">
        {{ partnerContent?.Brand }}
      </div>
    </a>
  </template>
</template>

<script setup lang="ts">
  import { openInDefaultBrowser, sendMixpanelEvent } from '~/utils/host-browser-apis'
  import { type DirectAd, useRandomDirectAd } from '~/composables/useRandomDirectAd'
  import type { NtpAdEvent } from '~/utils/host-browser-apis/analytics/index.types'

  interface DependenciesTypes {
    widgetId?: string
    gridPosition?: number
    counter?: string
    secondAdTile?: boolean
  }

  interface Props extends DependenciesTypes {
    fallback: 'outbrain' | 'taboola'
    onlyShowDirectedAdInPosition?: string | string[]
  }

  const props = defineProps<Props>()
  const isDirectedAdSlot = computed(() => {
    return (
      props.onlyShowDirectedAdInPosition !== undefined && props.counter === props.onlyShowDirectedAdInPosition
    )
  })
  const partnerContent = isDirectedAdSlot ? await useRandomDirectAd() : undefined

  let preventAdditionalLogCallsDueToReRenders = false

  onMounted(async () => {
    if (isDirectedAdSlot.value && partnerContent !== undefined && !preventAdditionalLogCallsDueToReRenders) {
      await sendMixpanelEvent('NtpAd_Rendered', convertParnerToMixpanelEvent(partnerContent))
      preventAdditionalLogCallsDueToReRenders = true
    }
  })

  const handleDirectAdClick = () => {
    const targetUrl = partnerContent?.['Click URL']
    const shouldOpenOnDefaultBrowser = partnerContent?.['Open In Default Browser']

    if (targetUrl && shouldOpenOnDefaultBrowser) {
      openInDefaultBrowser(targetUrl)
      propagateAdClicksToMixpanel()
      return
    }

    propagateAdClicksToMixpanel()
    window.open(targetUrl, '_blank')
  }

  async function propagateAdClicksToMixpanel() {
    if (partnerContent === undefined) return
    await sendMixpanelEvent('NtpAd_Clicked', convertParnerToMixpanelEvent(partnerContent))
  }

  const convertParnerToMixpanelEvent = (partner: DirectAd): NtpAdEvent => {
    return {
      ntp_ad_provider: 'direct',
      ntp_creative_id: partner['Creative Name'],
      ntp_ad_brand: partner.Brand,
      ntp_ad_clickurl: partner['Click URL'],
      ntp_ad_advertiser: partner.Advertiser,
      ntp_ad_campaign_name: partner['Campaign Name'],
      ntp_ad_open_with: partner?.['Open In Default Browser'] ? 'default-browser' : 'current-browser',
    }
  }

  function getImageLink() {
    if (partnerContent === undefined) return
    const creativeName = partnerContent?.['Creative Name']
    const selfHostedCreative = new URL(`/assets/images/partner/${creativeName}.webp`, import.meta.url).href
    if (!selfHostedCreative.includes('undefined')) {
      return selfHostedCreative
    }

    const imageUrl = partnerContent?.Image
    if (imageUrl) {
      return imageUrl
    }

    return 'https://picsum.photos/358/300?random=' + Math.floor(Math.random() * 1000)
  }
</script>
<style lang="scss" scoped>
  .news-v5 {
    display: grid;
    gap: 24px;
    margin: auto;
    max-width: 1272px;
    width: 100%;
    margin-top: 16px;

    &__item {
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      color: #212121;
      display: block;
      height: 358px;
      text-align: left;
      width: 300px;
      cursor: pointer;
    }

    &__item-image {
      background-position: center center;
      background-size: cover;
      height: 250px;
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
      animation: opacity-0-to-1-fade-in 1s;
    }

    &__item-content {
      height: 334px;

      &:hover {
        text-decoration: underline;
      }
    }

    &__item-title {
      margin: 12px 12px auto;
      max-width: 268px;
      height: 60px;
      font-size: 16px;
      line-height: 1.43;

      & > p {
        -webkit-line-clamp: 3;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }

    &__item-provider {
      margin-bottom: 12px;
      margin-right: 12px;
      font-size: 12px;
      line-height: 1.33;
      color: #757575;
    }
  }

  @keyframes opacity-0-to-1-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 1920px) {
    .news-v5 {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  @media (max-width: 1350px) {
    .news-v5 {
      grid-template-columns: repeat(3, 1fr);
      width: 948px;
    }
  }

  @media (max-width: 980px) {
    .news-v5 {
      grid-template-columns: repeat(2, 1fr);
      max-width: 624px;
    }
  }

  @media (max-width: 630px) {
    .news-v5 {
      grid-template-columns: repeat(1, 1fr);
      max-width: 300px;
    }
  }

  @media (min-width: 1920px) {
    .news-v5 {
      max-width: 1596px;
      grid-template-columns: repeat(5, 1fr);
    }
  }
</style>
